import React from 'react';
import { Link } from 'react-router-dom';
import {
  BRAND_NAME,
  CONTACT_FACEBOOK_URL,
  CONTACT_TELEGRAM_URL,
  CONTACT_YOUTUBE_URL,
} from '../../config/brand';

const Footer: React.FC = () => {
  const renderSocialIcon = (
    type: 'facebook' | 'youtube' | 'telegram',
    link: string | undefined,
    bgColor: string
  ) => {
    const icons = {
      facebook: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      youtube: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
      telegram: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      ),
    };

    return link ? (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center justify-center w-10 h-10 rounded-xl ${bgColor} text-white transition-all duration-300 hover:scale-105 hover:shadow-lg`}
        title={type.charAt(0).toUpperCase() + type.slice(1)}
        aria-label={type.charAt(0).toUpperCase() + type.slice(1)}
      >
        {icons[type]}
      </a>
    ) : null;
  };

  return (
    <footer className="bg-green-700 text-white" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand Section */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">
              <span className="text-gold-500">نهج</span> الأزهر
            </h3>
            <p className="text-green-100 text-sm leading-relaxed mb-4">
            محتوى تعليمي يليق بتميز طالب الثانوية الأزهرية            </p>
            <p className="text-gold-500 font-semibold text-sm">
              مستقبلك يبدأ من هنا
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">روابط سريعة</h4>
            <div className="space-y-2">
              <Link to="/" className="block text-green-100 hover:text-gold-500 transition-colors">
                الرئيسية
              </Link>
              <button 
                onClick={() => {
                  const teachersSection = document.getElementById('teachers-section');
                  if (teachersSection) {
                    teachersSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="block text-green-100 hover:text-gold-500 transition-colors w-full text-right"
              >
                المدرسين
              </button>
              <button 
                onClick={() => {
                  const coursesSection = document.getElementById('courses-section');
                  if (coursesSection) {
                    coursesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="block text-green-100 hover:text-gold-500 transition-colors w-full text-right"
              >
                الكورسات
              </button>
            </div>
          </div>

          {/* Contact Info - Social Media Only */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-white">تواصل معنا</h4>
            <div className="flex items-center gap-3 flex-wrap">
              {renderSocialIcon('telegram', CONTACT_TELEGRAM_URL, 'bg-sky-500 hover:bg-sky-400')}
              {renderSocialIcon('facebook', CONTACT_FACEBOOK_URL, 'bg-blue-600 hover:bg-blue-500')}
              {renderSocialIcon('youtube', CONTACT_YOUTUBE_URL, 'bg-red-600 hover:bg-red-500')}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-green-600 pt-6 text-center">
          <p className="text-green-100 text-sm mb-4">
            © 2026 {BRAND_NAME}. جميع الحقوق محفوظة.
          </p>
          
          {/* Developer Credits */}
          <div className="border-t border-green-600 pt-6 mt-6">
            <div className="text-center">
              <p className="text-green-100 text-sm mb-2">تم التطوير بواسطة</p>
              <div className="flex items-center justify-center gap-2">
                <a 
                  href="https://wa.me/201114706613" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="group cursor-pointer"
                >
                  <span className="text-xl font-bold bg-gradient-to-r from-gold-400 to-gold-600 bg-clip-text text-transparent group-hover:from-gold-300 group-hover:to-gold-500 transition-all duration-300">EduCraft</span>
                  <span className="text-2xl group-hover:scale-110 transition-transform duration-300">🚀</span>
                </a>
              </div>
              <p className="text-gold-400 text-xs font-medium mt-1">منصات تعليمية احترافية</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
